/**
 * @fileoverview Controlled center-pane WYSIWYG canvas for building a data report —
 *             surface tabs, paged report sheet, sortable column cards, slide-over
 *             field palette, and portaled column properties panel. Mirrors
 *             DocumentCanvasBuilder 1:1 adapted to the data-report domain.
 * @module ui-admin/components/ReportCanvasBuilder
 *
 * Responsibilities: - Render the canvas toolbar (Columns & Layout / Filters /
 *                     Grouping surfaces), first-visit tour prompt, and paged
 *                     canvas sheet containing a fixed Report Header card, the
 *                     @dnd-kit sortable CanvasColumnCards with "+ Insert field
 *                     here" affordances, and a fixed Totals footer card.
 *                   - Open the host-injected field palette inside a SlideOverPanel.
 *                   - Portal CanvasColumnPropertiesPanel (Content | Style) into the
 *                     host's right-pane Properties tab via propertiesPortalTarget.
 * Dependencies:     - @dnd-kit (drag-reorder), lucide-react, react-dom portal,
 *                     SlideOverPanel, BuilderHelpPanel, GuidedTour,
 *                     ComponentHelpPopover (per-column help), @firesquirrel/local-db
 *                     (types), @firesquirrel/core-domain (parameter types).
 * Architecture:     - Controlled component — no Dexie, no API, no theme context.
 *                     The host owns reportConfig/columns state and injects the
 *                     field palette content via renderPalette (its reportDataService
 *                     owns the real Dexie field catalog + live row counts).
 * Notes:            - Column order on the canvas is the report table's
 *                     left-to-right order (top card = leftmost column).
 *
 * @component ReportCanvasBuilder
 * Purpose:     Shared center-pane report canvas for tenant-web and system-web.
 * Props:       ReportCanvasBuilderProps.
 * Behavior:    Pure column/config operations emitted via onChange; selection is
 *              optionally controlled so the host's left-pane activation list and
 *              the canvas share one selection.
 * Data Source: none (controlled — host reads Dexie).
 * Constraints: presentation-only; no persistence, no parameter resolution.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Compass, X, Plus, Layers, FileText, Filter, Sigma, Trash2 } from 'lucide-react';
import type { ReportColumn, ReportConfig, ReportParameter } from '@firesquirrel/local-db';
import type { ReportConfigFilter } from '@firesquirrel/core-domain';
import { SlideOverPanel } from '../SlideOverPanel/SlideOverPanel';
import { BuilderHelpPanel } from '../BuilderHelpPanel/BuilderHelpPanel';
import { GuidedTour } from '../GuidedTour/GuidedTour';
import { REPORT_TOUR_STEPS, REPORT_TOUR_TARGET } from './reportTourSteps';
import { DEFAULT_REPORT_CONFIG, type ReportCanvasSurface, type ReportFieldCatalogEntry } from './reportDefaults';
import { CanvasColumnCard, columnKey } from './CanvasColumnCard';
import { CanvasColumnPropertiesPanel } from './CanvasColumnPropertiesPanel';
import { ReportCanvasToolbar } from './ReportCanvasToolbar';

/** localStorage key recording that the first-visit tour prompt has been seen. */
const TOUR_SEEN_KEY = 'report-builder-tour-seen';

function readTourSeen(): boolean {
  if (typeof window === 'undefined') return true;
  try { return window.localStorage.getItem(TOUR_SEEN_KEY) === 'true'; } catch { return false; }
}

function markTourSeen(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(TOUR_SEEN_KEY, 'true'); } catch { /* ignore */ }
}

/**
 * @contract ReportCanvasChange
 * Purpose: The patch shape the canvas emits — columns and/or reportConfig.
 * Fields: columns? (full replacement array), config? (partial ReportConfig).
 * Rules:  The host merges onto its own state and persists via its service.
 */
export interface ReportCanvasChange {
  columns?: ReportColumn[];
  config?: Partial<ReportConfig>;
}

/**
 * @contract ReportCanvasBuilderProps
 * Purpose: Props for the shared report canvas.
 * Fields:
 * - reportConfig: current report config (falls back to DEFAULT_REPORT_CONFIG).
 * - columns: ordered columns (canvas order = table order).
 * - parameters: declared report parameters (badge + binding dropdowns).
 * - reportName / reportDescription: shown on the fixed header card.
 * - selectedId / onSelectedIdChange: controlled column selection (columnKey =
 *   columnId ?? field — lets duplicated columns coexist).
 * - propertiesPortalTarget: host's right-pane Properties portal host.
 * - mode: 'edit' (authoring) or 'run' (read-only canvas).
 * - onChange: emit ReportCanvasChange patches.
 * - renderPalette: host-injected palette body shown inside the SlideOverPanel —
 *   receives onPick(field,label,type) which adds the column at insertIndex.
 * - sampleValues: optional per-field sample values for column previews.
 * - fieldCatalog / collectionCounts: passed through to renderPalette context.
 */
export interface ReportCanvasBuilderProps {
  reportConfig?: ReportConfig;
  columns: ReportColumn[];
  parameters: ReportParameter[];
  reportName?: string;
  reportDescription?: string;
  selectedId?: string | null;
  onSelectedIdChange?: (field: string | null) => void;
  propertiesPortalTarget?: HTMLElement | null;
  mode?: 'edit' | 'run';
  onChange: (patch: ReportCanvasChange) => void;
  renderPalette?: (ctx: {
    onPick: (field: string, label: string, type: string) => void;
    selectedFields: string[];
    insertIndex: number | null;
    parameters: ReportParameter[];
    fieldCatalog?: ReportFieldCatalogEntry[];
    collectionCounts?: Record<string, number>;
  }) => React.ReactNode;
  sampleValues?: Record<string, string[]>;
  fieldCatalog?: ReportFieldCatalogEntry[];
  collectionCounts?: Record<string, number>;
}

/** Thin "+" affordance between column cards opening the palette at an index. */
function InsertHere({ index, onInsert, trailing }: { index: number; onInsert: (i: number) => void; trailing?: boolean }) {
  return (
    <div className={`group/insert relative flex items-center justify-center ${trailing ? 'py-1.5' : 'py-0.5'}`}>
      <button
        type="button"
        onClick={() => onInsert(index)}
        data-testid={`insert-here-${index}`}
        aria-label="Insert field here"
        title="Insert a field here"
        className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-400 opacity-0 transition hover:border-orange-400 hover:text-orange-600 focus:opacity-100 group-hover/insert:opacity-100"
      >
        <Plus className="h-3 w-3" /> Insert field here
      </button>
    </div>
  );
}

/** Fixed canvas chrome card (header / footer) — non-draggable. */
function FixedChromeCard({ kind, title, subtitle, onDelete }: {
  kind: 'header' | 'footer'; title: string; subtitle?: string; onDelete?: () => void;
}) {
  return (
    <div data-testid={`report-${kind}-card`} className="mb-2 rounded-lg border border-gray-300 bg-gray-50 p-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
          {kind === 'header' ? 'Page header' : 'Page footer'}
        </span>
        <span className="font-semibold text-gray-800">{title}</span>
        <span className="flex-1" />
        {onDelete && (
          <button type="button" aria-label={`Delete report ${kind}`} onClick={onDelete}
            className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {subtitle && <p className="mt-1 text-[11px] text-gray-500">{subtitle}</p>}
    </div>
  );
}

export function ReportCanvasBuilder({
  reportConfig,
  columns,
  parameters,
  reportName = 'Untitled Report',
  reportDescription,
  selectedId: controlledSelectedId,
  onSelectedIdChange,
  propertiesPortalTarget,
  mode = 'edit',
  onChange,
  renderPalette,
  sampleValues,
  fieldCatalog,
  collectionCounts,
}: ReportCanvasBuilderProps) {
  const config = reportConfig ?? DEFAULT_REPORT_CONFIG;
  const [surface, setSurface] = useState<ReportCanvasSurface>('columns');
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const selectionControlled = controlledSelectedId !== undefined;
  const selectedId = selectionControlled ? controlledSelectedId : internalSelectedId;
  const setSelectedId = useCallback((id: string | null) => {
    if (!selectionControlled) setInternalSelectedId(id);
    onSelectedIdChange?.(id);
  }, [selectionControlled, onSelectedIdChange]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourSeen, setTourSeen] = useState<boolean>(() => readTourSeen());
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const startTour = useCallback(() => setTourActive(true), []);
  const finishTour = useCallback(() => { setTourActive(false); markTourSeen(); setTourSeen(true); }, []);
  const dismissTourPrompt = useCallback(() => { markTourSeen(); setTourSeen(true); }, []);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.visible !== false),
    [columns],
  );
  const canvasColumns = mode === 'edit' ? columns : visibleColumns;
  const columnIds = useMemo(() => canvasColumns.map(columnKey), [canvasColumns]);
  const filters = (config.filters ?? []) as ReportConfigFilter[];
  const parameterMap = useMemo(
    () => new Map(parameters.map((p) => [p.parameterId, p])),
    [parameters],
  );
  const selectedColumn = canvasColumns.find((c) => columnKey(c) === selectedId) ?? null;
  const firstField = canvasColumns[0] ? columnKey(canvasColumns[0]) : undefined;

  const emitColumns = useCallback((next: ReportColumn[]) => onChange({ columns: next }), [onChange]);
  const emitConfig = useCallback((next: Partial<ReportConfig>) => onChange({ config: next }), [onChange]);

  const openPaletteAt = useCallback((index: number | null) => {
    setInsertIndex(index);
    setPaletteOpen(true);
  }, []);

  const handlePick = useCallback((field: string, label: string, type: string) => {
    if (columns.some((c) => c.field === field)) {
      setPaletteOpen(false);
      setInsertIndex(null);
      setSelectedId(field);
      return;
    }
    const newColumn: ReportColumn = {
      field,
      label,
      visible: true,
      format: type === 'number' ? 'number' : type === 'date' ? 'date' : 'text',
    };
    const next = [...columns];
    next.splice(insertIndex ?? next.length, 0, newColumn);
    emitColumns(next);
    setSelectedId(field);
    setPaletteOpen(false);
    setInsertIndex(null);
  }, [columns, insertIndex, emitColumns, setSelectedId]);

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = columns.findIndex((c) => columnKey(c) === String(active.id));
    const to = columns.findIndex((c) => columnKey(c) === String(over.id));
    if (from === -1 || to === -1) return;
    const next = [...columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    emitColumns(next);
  }, [columns, emitColumns]);

  const moveColumn = useCallback((key: string, dir: -1 | 1) => {
    const from = columns.findIndex((c) => columnKey(c) === key);
    const to = from + dir;
    if (from === -1 || to < 0 || to >= columns.length) return;
    const next = [...columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    emitColumns(next);
  }, [columns, emitColumns]);

  const duplicateColumn = useCallback((key: string) => {
    const idx = columns.findIndex((c) => columnKey(c) === key);
    if (idx === -1) return;
    const src = columns[idx];
    const copy: ReportColumn = { ...src, columnId: `${key}-copy-${Date.now().toString(36)}`, label: `${src.label} (copy)` };
    const next = [...columns];
    next.splice(idx + 1, 0, copy);
    emitColumns(next);
  }, [columns, emitColumns]);

  const removeColumn = useCallback((key: string) => {
    emitColumns(columns.filter((c) => columnKey(c) !== key));
    if (selectedId === key) setSelectedId(null);
  }, [columns, emitColumns, selectedId, setSelectedId]);

  const patchSelectedColumn = useCallback((next: ReportColumn) => {
    const prev = columns.find((c) => columnKey(c) === selectedId);
    const nextColumns = columns.map((c) => (columnKey(c) === selectedId ? next : c));

    if (prev && prev.parameterId !== next.parameterId) {
      // Keep reportConfig.filters synchronized with column-level parameter bindings.
      const withoutOld = filters.filter(
        (f) => !(f.field === next.field && f.parameterId && f.parameterId === prev.parameterId),
      );
      let nextFilters = withoutOld;
      if (next.parameterId) {
        const boundParam = parameterMap.get(next.parameterId);
        const defaultOp =
          boundParam?.type === 'dateRange'
            ? 'inDateRange'
            : boundParam?.options?.multi
            ? 'in'
            : 'eq';
        const existingIdx = withoutOld.findIndex(
          (f) => f.field === next.field && f.parameterId === next.parameterId,
        );
        if (existingIdx === -1) {
          nextFilters = [
            ...withoutOld,
            { field: next.field, operator: defaultOp, value: null, parameterId: next.parameterId },
          ];
        }
      }
      onChange({
        columns: nextColumns,
        config: { filters: nextFilters as ReportConfig['filters'] },
      });
      return;
    }

    emitColumns(nextColumns);
  }, [columns, emitColumns, filters, onChange, parameterMap, selectedId]);

  const isEdit = mode === 'edit';

  // ------------------------- Filters surface ------------------------------
  const renderFiltersSurface = () => (
    <div className="flex flex-col gap-2" data-testid="filters-surface">
      {filters.length === 0 && parameters.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-400">
          <Filter className="h-8 w-8" />
          <p className="text-sm">No filters or parameters yet. Declare parameters in the left pane to drive filters at run time.</p>
        </div>
      )}
      {filters.map((f, i) => (
        <div key={`${f.field}-${i}`} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="font-mono text-xs text-gray-600">{f.field}</span>
          <span className="text-xs text-gray-500">{f.operator}</span>
          <span className="text-xs text-gray-700">{String(f.value)}</span>
          {f.parameterId && (
            <span className="rounded-sm border border-violet-200 px-1.5 py-px text-[10px] text-violet-700">
              🔗 {parameterMap.get(f.parameterId)?.label ?? f.parameterId}
            </span>
          )}
          {isEdit && (
            <button type="button" aria-label="Remove filter" className="ml-auto rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
              onClick={() => emitConfig({ filters: filters.filter((_, j) => j !== i) as ReportConfig['filters'] })}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {parameters.map((p) => (
        <div key={p.parameterId} className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
          <span className="rounded-sm border border-violet-200 px-1.5 py-px text-[10px] text-violet-700">Parameter</span>
          <span className="text-sm font-medium text-gray-700">{p.label}</span>
          <span className="font-mono text-[11px] text-gray-400">({p.parameterId})</span>
          <span className="text-[11px] text-gray-500">{p.type}{p.mode === 'relative' ? ' · relative' : ''}{p.required ? ' · required' : ''}</span>
        </div>
      ))}
    </div>
  );

  // ------------------------- Grouping surface -----------------------------
  const renderGroupingSurface = () => (
    <div className="flex flex-col gap-3" data-testid="grouping-surface">
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <label className="block text-[11px] font-medium text-gray-500">Group By</label>
        <select
          aria-label="Group by"
          disabled={!isEdit}
          value={(config.groupBy ?? [])[0] ?? ''}
          onChange={(e) => emitConfig({ groupBy: e.target.value ? [e.target.value] : [] })}
          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800"
        >
          <option value="">None</option>
          {columns.map((c) => <option key={c.field} value={c.field}>{c.label}</option>)}
        </select>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" disabled={!isEdit} checked={config.subtotals ?? false}
            onChange={(e) => emitConfig({ subtotals: e.target.checked })} />
          Show group subtotals
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" disabled={!isEdit} checked={config.grandTotal ?? false}
            onChange={(e) => emitConfig({ grandTotal: e.target.checked })} />
          Show grand total
        </label>
      </div>
      <div className="flex items-start gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 p-2.5 text-[11px] text-gray-500">
        <Sigma className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Grouping renders subtotal rows under each group and a grand-total footer — applied in the live preview and scheduled exports.
      </div>
    </div>
  );

  // ------------------------- Columns surface ------------------------------
  const renderColumnsSurface = () => (
    <>
      {canvasColumns.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center text-gray-400" data-testid="canvas-empty-state">
          <Layers className="h-8 w-8" />
          <p className="text-sm">Your report is empty. Add a field to start building.</p>
          {isEdit && (
            <button type="button" onClick={() => openPaletteAt(null)}
              className="mt-1 flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700">
              <Plus className="h-3.5 w-3.5" /> Add field
            </button>
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={columnIds} strategy={verticalListSortingStrategy}>
            {canvasColumns.map((col, index) => (
              <React.Fragment key={columnKey(col)}>
                {isEdit && <InsertHere index={index} onInsert={openPaletteAt} />}
                <CanvasColumnCard
                  column={col}
                  index={index}
                  total={canvasColumns.length}
                  selected={columnKey(col) === selectedId}
                  parameter={col.parameterId ? parameterMap.get(col.parameterId) : undefined}
                  sampleValues={sampleValues?.[col.field]}
                  tourCard={columnKey(col) === firstField}
                  tourMenu={columnKey(col) === firstField}

                  onSelect={() => setSelectedId(columnKey(col))}
                  onMove={(dir) => moveColumn(columnKey(col), dir)}
                  onDuplicate={() => duplicateColumn(columnKey(col))}
                  onDelete={() => removeColumn(columnKey(col))}
                  onToggleVisible={() => emitColumns(columns.map((c) => (columnKey(c) === columnKey(col) ? { ...c, visible: c.visible === false } : c)))}
                  onToggleHelp={() => setHelpOpen(true)}
                />
              </React.Fragment>
            ))}
            {isEdit && <InsertHere index={canvasColumns.length} onInsert={openPaletteAt} trailing />}
          </SortableContext>
        </DndContext>
      )}
    </>
  );

  return (
    <div className="relative flex min-h-[460px] flex-col" data-testid="report-canvas-builder">
      <style>{`@keyframes rcb-slide-in-right{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

      <ReportCanvasToolbar
        surface={surface}
        onSurfaceChange={setSurface}
        columnCount={columns.length}
        filterCount={filters.length}
        parameterCount={parameters.length}
        mode={mode}
        onOpenHelp={() => setHelpOpen(true)}
        onStartTour={startTour}
        onAddField={() => openPaletteAt(null)}
      />

      {!tourSeen && isEdit && (
        <div data-testid="tour-first-visit-prompt"
          className="mb-3 flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
          <Compass className="h-4 w-4 shrink-0" />
          <span className="flex-1">New to the report builder? Take a quick tour.</span>
          <button type="button" data-testid="tour-prompt-start"
            onClick={() => { dismissTourPrompt(); startTour(); }}
            className="rounded-md bg-orange-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-orange-700">
            Start tour
          </button>
          <button type="button" aria-label="Dismiss tour prompt" data-testid="tour-prompt-dismiss"
            onClick={dismissTourPrompt}
            className="rounded-sm p-1 text-orange-500 hover:bg-orange-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 justify-center bg-gray-100 py-6" data-testid="canvas-page" data-tour={REPORT_TOUR_TARGET.canvas}>
          <div className="w-full max-w-[816px] rounded-lg border border-gray-300 bg-white p-8 shadow-xs">
            {surface === 'columns' && (
              <>
                <FixedChromeCard
                  kind="header"
                  title={reportName}
                  subtitle={reportDescription ?? 'Shows report title, branding, run date & resolved parameter summary.'}
                />
                {renderColumnsSurface()}
                <FixedChromeCard
                  kind="footer"
                  title={config.grandTotal ? 'Grand totals' : 'Report footer'}
                  subtitle={config.grandTotal ? 'Grand Total: Count · Page X of Y' : 'Page X of Y'}
                />
              </>
            )}
            {surface === 'filters' && (
              <>
                <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                  <FileText className="h-3.5 w-3.5" /> Report filters & parameters
                </div>
                {renderFiltersSurface()}
              </>
            )}
            {surface === 'grouping' && (
              <>
                <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                  <Sigma className="h-3.5 w-3.5" /> Grouping & totals
                </div>
                {renderGroupingSurface()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Field palette slide-over — content injected by the host (its
          reportDataService owns the real Dexie catalog + row counts). */}
      <SlideOverPanel
        side="left"
        open={paletteOpen}
        onClose={() => { setPaletteOpen(false); setInsertIndex(null); }}
        title="Add Report Field or Parameter"
        width={340}
      >
        {renderPalette ? renderPalette({
          onPick: handlePick,
          selectedFields: columns.map((c) => c.field),
          insertIndex,
          parameters,
          fieldCatalog,
          collectionCounts,
        }) : (
          <div className="p-3 text-sm text-gray-400">No field palette configured.</div>
        )}
      </SlideOverPanel>

      {/* Column properties — portaled into the host's right-pane Properties tab
          when provided; otherwise a fixed right overlay (same contract as
          DocumentCanvasBuilder). */}
      {selectedColumn && isEdit && (() => {
        const usePortalMode = propertiesPortalTarget !== undefined;
        if (usePortalMode && !propertiesPortalTarget) return null;
        const panel = (
          <aside
            role="complementary"
            aria-label="Column properties"
            data-testid="column-properties-aside"
            className={usePortalMode
              ? 'flex h-full w-full flex-col bg-white overflow-hidden'
              : 'fixed right-0 top-0 bottom-0 z-50 flex w-80 flex-col border-l border-gray-200 bg-white shadow-2xl overflow-hidden'}
            style={usePortalMode ? undefined : { animation: 'rcb-slide-in-right 0.2s ease-out' }}
          >
            <CanvasColumnPropertiesPanel
              column={selectedColumn}
              parameters={parameters}
              onChange={patchSelectedColumn}
              onClose={() => setSelectedId(null)}
            />
          </aside>
        );
        return usePortalMode && propertiesPortalTarget
          ? createPortal(panel, propertiesPortalTarget)
          : panel;
      })()}

      <BuilderHelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onStartTour={() => { setHelpOpen(false); startTour(); }}
      />
      <GuidedTour steps={REPORT_TOUR_STEPS} active={tourActive} onComplete={finishTour} />
    </div>
  );
}

export type { ReportCanvasSurface, ReportFieldCatalogEntry } from './reportDefaults';
export { DEFAULT_REPORT_CONFIG } from './reportDefaults';
export { REPORT_TOUR_STEPS, REPORT_TOUR_TARGET } from './reportTourSteps';
export { columnActivationList } from './columnActivationList';
export type { ColumnActivationEntry } from './columnActivationList';
